/**
 * Mampe gift choice — threshold popup (spec: docs/task-geschenk-auswahl.md).
 * Ported from Aseli-Store snippet-aseli-gift-choice.js (Dawn) to the Impact theme.
 *
 * Server side the action is an AUTOMATIC BxGY discount ("spend >= threshold, get
 * 1 of the listed products free", no code). This script never decides about money:
 * it opens the popup when the cart crosses the threshold, adds the chosen variant
 * as a _gift_choice line (quantity always exactly 1) and removes that line when
 * the value drops below the threshold again. Catalog: ACTIVE gift_choice_action
 * metaobjects, serialized by snippets/mampe-gift-choice.liquid
 * (<script data-mampe-gift-choice-catalog> — rendered even with zero active
 * actions, so pause/ended cleanup keeps working).
 *
 * Rules (pilot 2026-10-21, "auswahl89" ab 89 EUR, see spec §9 — ORDER MATTERS):
 *  1. Foreign code gift (platform backoff, non-Plus): all products in a BxGY are
 *     locked for other product discounts and spend-threshold BxGYs never both
 *     apply in one order — a _gift line or an active code-gift session (codes from
 *     the data-mampe-gift-catalog blob of the code widget, plus this action's
 *     exclusiveCodes) displaces our automatic BxGY. Remove our line, stay silent.
 *  2. Choice line present: auto-remove only when the LOOSE base (pre-order-discount
 *     line sum) drops below the threshold — Shopify checks the BxGY minimum BEFORE
 *     order discounts, so the theme must never drop a gift the checkout grants.
 *     2b. Action vanished from the catalog (paused/ended): remove the line + one
 *         shot notice "Die Geschenk-Aktion ist leider beendet." (MAMPE addition —
 *         Aseli leaves the line in place, where it would ring up at full price).
 *     2c. Displaced line (MAMPE precision of the Aseli gch_displaced pattern):
 *         choice line charged at full price while OTHER lines carry line-level
 *         discounts — a competing product discount (e.g. automatic bottle
 *         percentage promos) took the order's only free unit. Remove silently.
 *         The "other lines have allocations" counter-proof is required on
 *         purpose: during the metaobject-only staging phase (no BxGY yet) EVERY
 *         choice line is undiscounted — that phase must keep the line for the
 *         UX walkthrough (spec §8 Phase 6).
 *  3. Below-threshold actions get their seen flag cleared -> next crossing pops
 *     again (STRICT base: cart.total_price minus gift lines, inclusive of coupons).
 *  4. Show the popup for the HIGHEST met threshold, unseen, and only while NO
 *     entitled line is in the cart (Shopify gifts the FIRST entitled line — a
 *     self-bought pool product would steal the free unit from the customer's pick).
 *
 * CONFIGURATOR MODEL (user decision 2026-10-21, replaces the old block rule):
 * configurator lines are NOT blocked — like tickets and vouchers they simply
 * never count toward the threshold (blockedHandles ∪ Teeinblue sniff). The
 * server mirrors this via the gift-eligible buy collection (TITLE NOT_CONTAINS
 * Konfigurator), so theme and checkout can never contradict each other. The
 * old contradiction — theme notice "nicht kombinierbar" while checkout gifted
 * the self-bought pool line anyway — died with this model.
 *
 * Threshold bases exclude gift lines AND Ticket/Einkaufsgutschein lines AND
 * configurator lines (excludeTypes/blockedHandles from the catalog — server
 * side mirrored in the gift-eligible products buy collection).
 *
 * Impact specifics (vs Dawn, idioms proven in mampe-cart-gift.js):
 *  - Cart events: document event `cart:change` (detail {cart, baseEvent});
 *    our own raw fetch calls do NOT fire it. We ALSO listen to `cart:refresh`
 *    (dispatched by the code-gift widget after ITS mutations — replaces Aseli's
 *    RETIRE_EVENT bridge, which would require touching mampe-cart-gift.js).
 *  - After our own cart mutations: dispatch `cart:refresh` on documentElement +
 *    pageType-cart reload (refreshTheme idiom from mampe-cart-gift.js).
 *  - Modal: Impact <dialog-element> (show()/hide(), [open] attribute) — the
 *    confetti observer watches that attribute.
 *  - NO drawer deferral (removed 2026-10-21, user finding): the popup opens
 *    IMMEDIATELY on the threshold crossing, ON TOP of the open cart drawer
 *    (modal z 1100 > drawer/backdrop 999 — verified via the tile-CTA flow).
 *    Waiting for the drawer to close hid the popup from everyone who goes
 *    straight to checkout from the drawer.
 *  - Impact swaps cart regions via innerHTML — hydration via MutationObserver,
 *    guarded writes only (setText), never observers on own DOM.
 */
(function () {
  'use strict';

  // Rendered exactly once by theme.liquid — the guard is defensive only.
  if (window.mampeGiftChoiceInit) return;
  window.mampeGiftChoiceInit = true;

  var SOURCE = 'mampe-gift-choice';
  var GIFT_PROP = '_gift_choice';
  var FOREIGN_GIFT_PROP = '_gift'; // line property of the code-gift widget
  var SS_KEY = 'mampe:gift-choice-seen'; // one popup per threshold crossing (session)
  var LS_NOTICE_KEY = 'mampe:gift-choice-notice'; // read-once, 2-minute TTL
  var NOTICE_TTL = 120000;

  var SELECTORS = {
    catalog: '[data-mampe-gift-choice-catalog]',
    config: '[data-mampe-gift-choice-config]',
    modal: '[data-mampe-gift-choice-modal]',
    grid: '[data-mampe-gift-choice-grid]',
    subline: '[data-gc-subline]',
    status: '[data-mampe-gift-choice-status]',
    select: '[data-gc-select]',
    dismiss: '[data-mampe-gift-choice-dismiss]',
    overlay: '[data-mampe-gift-choice-overlay]',
    open: '[data-gc-open]',
    swap: '[data-gc-swap]',
    notice: '[data-mampe-gift-choice-notice]',
    lineRemove: '[data-mampe-gift-choice-line-remove]',
  };

  var state = {
    actions: [], // catalog entries [{handle, threshold, products, blockedHandles[], excludeTypes[]}]
    blockedHandles: {}, // union over active actions (configurator gate, rule 1)
    excludeTypes: {}, // union over active actions (threshold base filter)
    exclusive: {}, // this action's exclusive regular codes (lowercased)
    cart: null, // last cart snapshot
    busy: false, // cart mutation in flight
    refreshing: false, // full-snapshot fetch in flight
    notice: null, // 'ended' — one-shot slot text
  };

  var fmtEUR = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });

  function routes() {
    var el = document.querySelector(SELECTORS.catalog);
    if (el) {
      try {
        var parsed = JSON.parse(el.textContent);
        if (parsed && parsed.routes && parsed.routes.cartUrl) return parsed.routes;
      } catch (err) {
        /* fall through to defaults */
      }
    }
    return { cartUrl: '/cart', cartAddUrl: '/cart/add', cartChangeUrl: '/cart/change' };
  }

  // ---------------------------------------------------------------- catalog / session flags

  function splitList(raw) {
    var out = [];
    var parts = String(raw || '').split(',');
    for (var i = 0; i < parts.length; i++) {
      var part = parts[i].trim();
      if (part) out.push(part.toLowerCase());
    }
    return out;
  }

  function readActions() {
    var actions = [];
    var nodes = document.querySelectorAll(SELECTORS.catalog);
    for (var i = 0; i < nodes.length; i++) {
      try {
        var parsed = JSON.parse(nodes[i].textContent);
        var list = (parsed && parsed.actions) || [];
        for (var a = 0; a < list.length; a++) {
          var action = list[a];
          if (action && action.handle && action.threshold && action.products) {
            action.blockedHandles = splitList(action.blockedHandles);
            action.excludeTypes = splitList(action.excludeTypes);
            action.exclusiveCodes = splitList(action.exclusiveCodes);
            actions.push(action);
          }
        }
      } catch (err) {
        /* malformed entry — skip, never break the storefront */
      }
    }
    state.actions = actions;
    state.blockedHandles = {};
    state.excludeTypes = {};
    state.exclusive = {};
    for (var k = 0; k < actions.length; k++) {
      for (var b = 0; b < actions[k].blockedHandles.length; b++) state.blockedHandles[actions[k].blockedHandles[b]] = true;
      for (var t = 0; t < actions[k].excludeTypes.length; t++) state.excludeTypes[actions[k].excludeTypes[t].toLowerCase()] = true;
      for (var e = 0; e < actions[k].exclusiveCodes.length; e++) state.exclusive[actions[k].exclusiveCodes[e]] = true;
    }
  }

  function findAction(handle) {
    for (var i = 0; i < state.actions.length; i++) {
      if (state.actions[i].handle === handle) return state.actions[i];
    }
    return null;
  }

  // Code-gift catalog of the code widget (rendered globally by theme.liquid) —
  // only the CODES matter: an active session of any of them displaces our
  // automatic BxGY (rule 2), even before a gift line exists.
  function readGiftCodes() {
    var codes = {};
    var nodes = document.querySelectorAll('[data-mampe-gift-catalog]');
    for (var i = 0; i < nodes.length; i++) {
      try {
        var parsed = JSON.parse(nodes[i].textContent);
        var gifts = (parsed && parsed.gifts) || [];
        for (var g = 0; g < gifts.length; g++) {
          if (gifts[g] && gifts[g].code) codes[String(gifts[g].code).toLowerCase()] = true;
        }
      } catch (err) {
        /* no catalog → session check degrades to the _gift line check */
      }
    }
    return codes;
  }
  var giftCodes = {}; // populated in init (catalog blob is static per page load)

  function seenMap() {
    try {
      return JSON.parse(sessionStorage.getItem(SS_KEY) || '{}') || {};
    } catch (err) {
      return {};
    }
  }

  function markSeen(handle) {
    var map = seenMap();
    map[handle] = true;
    try {
      sessionStorage.setItem(SS_KEY, JSON.stringify(map));
    } catch (err) {
      /* private mode — popup may re-show after reload, acceptable */
    }
  }

  function isSeen(handle) {
    return !!seenMap()[handle];
  }

  function clearSeen(handle) {
    var map = seenMap();
    if (!map[handle]) return;
    delete map[handle];
    try {
      sessionStorage.setItem(SS_KEY, JSON.stringify(map));
    } catch (err) {
      /* see markSeen */
    }
  }

  // ---------------------------------------------------------------- notices (one-shot, TTL)

  // Mirrored to localStorage so the notice survives the /cart reload that
  // refreshTheme() triggers — same pattern as mampe-cart-gift.js (read-once,
  // 2-minute TTL). Text templates live on the ALWAYS-rendered config div.
  // ('ended' is the only notice since the configurator block was retired —
  // configurators now just never count toward the threshold, user decision
  // 2026-10-21.)
  function noticeText(type) {
    if (type !== 'ended') return '';
    var el = document.querySelector(SELECTORS.config);
    var v = el ? el.getAttribute('data-gc-tpl-notice_' + type) : null;
    return v != null && v !== '' ? v : '';
  }

  function storeNotice(type) {
    state.notice = type;
    try {
      localStorage.setItem(LS_NOTICE_KEY, JSON.stringify({ type: type, at: Date.now() }));
    } catch (err) {
      /* in-memory notice still covers the drawer */
    }
    fillNotices();
  }

  function consumeNotice() {
    try {
      var saved = JSON.parse(localStorage.getItem(LS_NOTICE_KEY) || 'null');
      if (saved && saved.type === 'ended'
          && Date.now() - (saved.at || 0) < NOTICE_TTL) {
        state.notice = saved.type;
      }
      localStorage.removeItem(LS_NOTICE_KEY);
    } catch (err) {
      /* ignore */
    }
  }

  function setText(el, text) {
    // Only touch the DOM when the value actually changes — writing the SAME
    // string still replaces the text node, which feeds the MutationObserver
    // below and would create an endless hydrate loop.
    if (el && el.textContent !== text) el.textContent = text;
  }

  function fillNotices() {
    var text = noticeText(state.notice);
    var slots = document.querySelectorAll(SELECTORS.notice);
    for (var i = 0; i < slots.length; i++) {
      setText(slots[i], text);
      if (slots[i].hidden === !!text) slots[i].hidden = !text; // only write on change
    }
  }

  function hydrateNoticeSlots() {
    var slots = document.querySelectorAll(SELECTORS.notice + ':not([data-hydrated])');
    if (!slots.length) return;
    for (var i = 0; i < slots.length; i++) slots[i].setAttribute('data-hydrated', '1');
    fillNotices();
  }

  // ---------------------------------------------------------------- cart helpers

  function isChoiceLine(item) {
    return !!(item && item.properties && item.properties[GIFT_PROP]);
  }

  function isForeignGiftLine(item) {
    return !!(item && item.properties && item.properties[FOREIGN_GIFT_PROP]);
  }

  function choiceLine(cart) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (isChoiceLine(items[i])) return items[i];
    }
    return null;
  }

  function foreignGiftLine(cart) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (isForeignGiftLine(items[i])) return items[i];
    }
    return null;
  }

  // Configurator detection (double signal): blocked handle from the catalog OR
  // a Teeinblue fingerprint on the line (key prefix _teeinblue / value
  // cdn.teeinblue.com — same heuristic as line-item.liquid). The sniff also
  // catches future configurator products without a catalog update. Configurator
  // lines are NOT blocked — they just never count toward the threshold (see
  // header, user decision 2026-10-21).
  function isConfiguratorLine(item) {
    if (!item) return false;
    if (state.blockedHandles[String(item.handle || '').toLowerCase()]) return true;
    var props = item.properties || {};
    for (var key in props) {
      if (Object.prototype.hasOwnProperty.call(props, key)) {
        if (String(key).indexOf('_teeinblue') === 0) return true;
        var value = String(props[key] || '');
        if (value.indexOf('cdn.teeinblue.com') >= 0) return true;
      }
    }
    return false;
  }

  // Ticket / Einkaufsgutschein lines never count toward the threshold (spec §4.3)
  function isExcludedType(item) {
    return !!(item && state.excludeTypes[String(item.product_type || '').toLowerCase()]);
  }

  // A code-gift SESSION (cart.discount_codes) displaces our automatic BxGY even
  // before its gift line exists — a sold-out gift product never produces a line
  // at all, but the code still wins the order's only free unit (non-Plus limit).
  function activeGiftCodeSession(cart) {
    var codes = (cart && cart.discount_codes) || [];
    for (var i = 0; i < codes.length; i++) {
      var c = codes[i] && String(codes[i].code || '').toLowerCase();
      if (c && (giftCodes[c] || state.exclusive[c])) return true;
    }
    return false;
  }

  // First cart line over ANY of the action's products (regular purchase, foreign
  // gift — line properties don't matter). Shopify's automatic BxGY gifts the
  // FIRST entitled line in the cart, so as long as none exists, the line this
  // popup adds deterministically wins the free unit.
  function entitledLine(cart, action) {
    var items = (cart && cart.items) || [];
    var pool = {};
    var products = (action && action.products) || [];
    for (var p = 0; p < products.length; p++) pool[products[p].handle] = true;
    for (var i = 0; i < items.length; i++) {
      if (pool[items[i].handle]) return items[i];
    }
    return null;
  }

  // LOOSE threshold base (mirrors the server): sum of final_line_price over
  // lines that are neither gift (_gift / _gift_choice) nor Ticket/voucher nor
  // configurator, BEFORE order-level discounts. Shopify checks the automatic
  // BxGY minimum BEFORE order discounts — auto-remove must use this base or the
  // theme would drop gifts the checkout still grants (Aseli asymmetry, verified
  // 2026-09-02).
  function looseBase(cart) {
    var items = (cart && cart.items) || [];
    var total = 0;
    for (var i = 0; i < items.length; i++) {
      if (!isChoiceLine(items[i]) && !isForeignGiftLine(items[i]) && !isExcludedType(items[i])
          && !isConfiguratorLine(items[i])) {
        total += items[i].final_line_price;
      }
    }
    return total;
  }

  // STRICT threshold base (popup gate): cart.total_price is post ALL visible
  // discounts, so subtracting gift lines, excluded-type lines AND configurator
  // lines yields the post-coupon value of everything that counts. The threshold
  // must hold INCLUSIVE of coupons (Aseli, 2026-09-02). Without an order-code
  // session this equals looseBase.
  function strictBase(cart) {
    var items = (cart && cart.items) || [];
    var subtract = 0;
    for (var i = 0; i < items.length; i++) {
      if (isChoiceLine(items[i]) || isForeignGiftLine(items[i]) || isExcludedType(items[i])
          || isConfiguratorLine(items[i])) {
        subtract += items[i].final_line_price;
      }
    }
    return ((cart && cart.total_price) || 0) - subtract;
  }

  // Full cart snapshots come from GET /cart.js. `cart:change` usually carries a
  // full snapshot, but partial payloads (only new lines, no total_price) must
  // trigger a one-shot refresh before any decision (Aseli live incident
  // 2026-10-19: a partial publish left the choice line next to an unseen
  // foreign gift line — the checkout charged it).
  function isFullSnapshot(cart) {
    return !!(cart && typeof cart.total_price === 'number' && Array.isArray(cart.items));
  }

  function withFreshCart(fn) {
    fetch(routes().cartUrl + '.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(fn)
      .catch(function () {});
  }

  function refreshCartThenEvaluate() {
    if (state.refreshing) return; // a fetch in flight already decides on fresh data
    state.refreshing = true;
    fetch(routes().cartUrl + '.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        state.refreshing = false;
        state.cart = cart;
        evaluate();
      })
      .catch(function () {
        state.refreshing = false;
        /* keep the last snapshot; decisions re-run on the next cart event */
      });
  }

  // ---------------------------------------------------------------- impact refresh

  function refreshTheme() {
    // Maestrooo-documented way to make Impact re-render its cart UI (count,
    // drawer, cart page) after mutations it did not perform itself.
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    // cart:refresh does NOT re-render the cart PAGE's main section — Impact
    // page-reloads there after its own mutations, so we follow the same idiom
    // (idiom from mampe-cart-gift.js; notices survive via localStorage).
    if (window.themeVariables && window.themeVariables.settings
        && window.themeVariables.settings.pageType === 'cart') {
      window.location.reload();
    }
  }

  // ---------------------------------------------------------------- popup UI

  function modalEl() {
    return document.querySelector(SELECTORS.modal);
  }

  function modalOpen() {
    var modal = modalEl();
    return !!(modal && modal.hasAttribute('open'));
  }

  // Copy lives on the modal root / config div as data-gc-tpl-* attributes —
  // German literals (DE-only shop, same precedent as mampe-cart-gift.liquid).
  function tpl(name, fallback) {
    var el = modalEl() || document.querySelector(SELECTORS.config);
    var v = el ? el.getAttribute('data-gc-tpl-' + name) : null;
    return v != null && v !== '' ? v : (fallback || '');
  }

  function fill(text, vars) {
    return String(text).replace(/\{(\w+)\}/g, function (m, key) {
      return vars[key] != null ? vars[key] : m;
    });
  }

  // Card DNA (MAMPE-CI, spec §4.6): uniform creme stage + transparent PNG,
  // title in system font semibold, squared outline CTA "AUSWÄHLEN" (caps,
  // bold) — no per-card badge, the modal headline carries the context.
  var CART_ICON =
    '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
    '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"/></svg>';

  function buildCard(action, product) {
    var li = document.createElement('li');
    li.className = 'mampe-gc-modal__card';

    var media = document.createElement('div');
    media.className = 'mampe-gc-modal__card-media';
    if (product.image) {
      var img = document.createElement('img');
      img.src = product.image;
      img.alt = '';
      img.loading = 'lazy';
      media.appendChild(img);
    }

    var body = document.createElement('div');
    body.className = 'mampe-gc-modal__card-body';

    var title = document.createElement('h3');
    title.className = 'mampe-gc-modal__card-title';
    title.textContent = product.title;

    var btn = document.createElement('button');
    btn.type = 'button';
    // plain button, NOT is="custom-button": its loading spinner only clears on
    // pageshow (spinner bug, mampe-cart-gift 6. Runde) — same rule as the code
    // widget's submit button.
    btn.className = 'mampe-gc-modal__select';
    btn.setAttribute('data-gc-select', '');
    btn.setAttribute('data-gc-variant', String(product.variantId));
    btn.setAttribute('data-gc-action', action.handle);
    btn.setAttribute('aria-label', fill(tpl('select_aria', '{title} als Geschenk auswählen'), { title: product.title }));
    btn.innerHTML = CART_ICON;
    var label = document.createElement('span');
    label.textContent = tpl('select', 'AUSWÄHLEN');
    btn.appendChild(label);

    body.appendChild(title);
    body.appendChild(btn);
    li.appendChild(media);
    li.appendChild(body);
    return li;
  }

  function renderPopup(action) {
    var modal = modalEl();
    if (!modal) return;
    var grid = modal.querySelector(SELECTORS.grid);
    if (grid) {
      grid.innerHTML = '';
      var products = action.products || [];
      for (var i = 0; i < products.length; i++) grid.appendChild(buildCard(action, products[i]));
    }
    setText(
      modal.querySelector(SELECTORS.subline),
      fill(tpl('subline', 'Ab {threshold} Warenwert gibt’s eine Flasche deiner Wahl gratis obendrauf.'), {
        threshold: fmtEUR.format(action.threshold / 100),
      })
    );
    setText(modal.querySelector(SELECTORS.status), '');
    markSeen(action.handle); // one popup per crossing — dismiss and pick both count
    if (typeof modal.show === 'function') modal.show();
    else modal.setAttribute('open', ''); // custom element missing — basic fallback
  }

  function hideModal() {
    var modal = modalEl();
    if (!modal) return;
    if (typeof modal.hide === 'function') modal.hide();
    else modal.removeAttribute('open');
  }

  // ---------------------------------------------------------------- confetti rain
  //
  // Own canvas rain — no library, no CDN. Runs while the popup is open: on
  // desktop the pieces fall in the strips LEFT and RIGHT of the modal card; on
  // narrow viewports they rain across the full width behind the dimmed overlay.
  // The canvas sits one layer BELOW the modal (z-index 79 vs 80, see CSS) so
  // nothing ever covers the product cards; pointer-events: none keeps it inert.
  // Respects prefers-reduced-motion (never starts). Driven by a READ-ONLY
  // MutationObserver on the modal's [open] attribute — every open/close path
  // (show(), hide(), ESC, backdrop, close button) flips that attribute.
  // MAMPE palette (spec §4.6): rot, dunkles rot, creme, weiß, schwarz.

  var CONFETTI_COLORS = [
    '#dd0817', // mampe rot
    '#a00510', // dunkles rot
    '#f5f0e6', // creme
    '#ffffff', // weiß
    '#060606', // schwarz
  ];

  var confetti = { canvas: null, ctx: null, raf: 0, parts: [], last: 0, running: false };

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function confettiStrips() {
    var vw = window.innerWidth;
    var strips = [];
    var card = modalEl() && modalEl().querySelector('.mampe-gc-modal__content');
    if (card) {
      var rect = card.getBoundingClientRect();
      if (rect.width < vw * 0.7) {
        strips.push([0, Math.max(0, rect.left - 16)]);
        strips.push([Math.min(vw, rect.right + 16), vw]);
      }
    }
    if (!strips.length || strips[0][1] < 30) strips = [[0, vw]]; // mobile / degenerate rects
    return strips;
  }

  function spawnParticle(strips, vh, seedTop) {
    var strip = strips[Math.floor(Math.random() * strips.length)];
    return {
      x: strip[0] + Math.random() * Math.max(1, strip[1] - strip[0]),
      y: seedTop ? Math.random() * vh * 0.5 : -20 - Math.random() * 60,
      w: 6 + Math.random() * 5,
      h: 8 + Math.random() * 6,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      rot: Math.random() * Math.PI * 2,
      vr: (Math.random() - 0.5) * 4, // rad/s
      vy: 70 + Math.random() * 90, // px/s
      sway: 12 + Math.random() * 18, // px/s horizontal drift amplitude
      freq: 0.8 + Math.random() * 1.2,
      phase: Math.random() * Math.PI * 2,
    };
  }

  function confettiCap() {
    return window.innerWidth < 750 ? 40 : 90;
  }

  function confettiFrame(now) {
    if (!confetti.running) return;
    var vw = window.innerWidth;
    var vh = window.innerHeight;
    var dt = Math.min(0.05, (now - confetti.last) / 1000) || 0.016;
    confetti.last = now;
    confetti.ctx.clearRect(0, 0, vw, vh);
    for (var i = 0; i < confetti.parts.length; i++) {
      var p = confetti.parts[i];
      p.y += p.vy * dt;
      p.x += Math.cos((now / 1000) * p.freq * Math.PI + p.phase) * p.sway * dt;
      p.rot += p.vr * dt;
      if (p.y > vh + 30) {
        confetti.parts[i] = spawnParticle(confettiStrips(), vh, false);
        p = confetti.parts[i];
      }
      confetti.ctx.save();
      confetti.ctx.translate(p.x, p.y);
      confetti.ctx.rotate(p.rot);
      confetti.ctx.fillStyle = p.color;
      confetti.ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h * 0.6);
      confetti.ctx.restore();
    }
    confetti.raf = requestAnimationFrame(confettiFrame);
  }

  function startConfetti() {
    if (confetti.running || prefersReducedMotion()) return;
    if (!confetti.canvas) {
      confetti.canvas = document.createElement('canvas');
      confetti.canvas.className = 'mampe-gc-confetti';
      confetti.canvas.setAttribute('aria-hidden', 'true');
      confetti.ctx = confetti.canvas.getContext('2d');
    }
    document.body.appendChild(confetti.canvas); // appendChild moves it if already present
    var vw = window.innerWidth;
    var vh = window.innerHeight;
    var dpr = Math.min(2, window.devicePixelRatio || 1);
    confetti.canvas.width = Math.round(vw * dpr);
    confetti.canvas.height = Math.round(vh * dpr);
    confetti.canvas.style.width = vw + 'px';
    confetti.canvas.style.height = vh + 'px';
    confetti.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    confetti.parts = [];
    var cap = confettiCap();
    var strips = confettiStrips();
    for (var i = 0; i < cap; i++) confetti.parts.push(spawnParticle(strips, vh, true));
    confetti.running = true;
    confetti.last = performance.now();
    confetti.raf = requestAnimationFrame(confettiFrame);
  }

  function stopConfetti() {
    if (!confetti.running) return;
    confetti.running = false;
    cancelAnimationFrame(confetti.raf);
    confetti.parts = [];
    if (confetti.ctx) confetti.ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
    if (confetti.canvas && confetti.canvas.parentNode) confetti.canvas.parentNode.removeChild(confetti.canvas);
  }

  // READ-ONLY observer on the modal's [open] attribute — the single source of
  // truth for "popup is showing", covering ESC/backdrop/close-button too.
  function watchModal() {
    var modal = modalEl();
    if (!modal) return;
    new MutationObserver(function () {
      if (modal.hasAttribute('open')) startConfetti();
      else stopConfetti();
    }).observe(modal, { attributes: true, attributeFilter: ['open'] });
  }

  // ---------------------------------------------------------------- choice mutations

  function addChoice(button) {
    if (state.busy) return;
    var variantId = Number(button.getAttribute('data-gc-variant'));
    var action = findAction(button.getAttribute('data-gc-action'));
    if (!variantId || !action) return;
    state.busy = true;
    var props = {};
    props[GIFT_PROP] = action.handle; // quantity always exactly 1 (spec §4.1)
    fetch(routes().cartAddUrl + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1, properties: props }],
      }),
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (cartData) {
        state.cart = cartData;
        refreshTheme();
        hideModal();
      })
      .catch(function () {
        setText(modalEl() && modalEl().querySelector(SELECTORS.status), tpl('status_error', 'Das hat leider nicht geklappt. Bitte versuch es gleich noch einmal.'));
      })
      .finally(function () {
        state.busy = false;
      });
  }

  function removeChoice(line, after) {
    if (state.busy || !line) return;
    state.busy = true;
    // NOTE: `line.key` must come from a snapshot taken AFTER any /discount/
    // visit — the visit regenerates all line keys, stale keys die with HTTP 400
    // (Aseli live finding 2026-10-20; swapFromCart decides on fresh data).
    fetch(routes().cartChangeUrl + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: line.key, quantity: 0 }),
    })
      .then(function (response) {
        // A 400 with a JSON body must NOT pass as success — the callback would
        // run on garbage (e.g. open the swap popup while the old line survives).
        if (!response.ok) throw new Error('cart/change ' + response.status);
        return response.json();
      })
      .then(function (cartData) {
        state.cart = cartData;
        refreshTheme();
        if (typeof after === 'function') after();
      })
      .catch(function () {
        setText(modalEl() && modalEl().querySelector(SELECTORS.status), tpl('status_error', 'Das hat leider nicht geklappt. Bitte versuch es gleich noch einmal.'));
      })
      .finally(function () {
        state.busy = false;
      });
  }

  // ---------------------------------------------------------------- cart strip hooks

  // Manual open from the cart strip button (mampe-gift-choice-hint.liquid). The
  // strip only renders the button in safe states, but re-check the guards
  // against the latest cart snapshot anyway: a foreign gift line, code-gift
  // session or an entitled line would all make a pick a bad promise.
  // (Configurators are NOT a guard anymore — they just never count toward the
  // threshold, see header. The strip button only renders when the base is met.)
  function openFromCart(handle) {
    var action = findAction(handle);
    if (!action || !state.cart) return;
    if (foreignGiftLine(state.cart) || activeGiftCodeSession(state.cart) || entitledLine(state.cart, action)) return;
    renderPopup(action);
  }

  // "tauschen" on the strip's picked state: remove the current choice line, then
  // offer the popup again. After removal the cart may hold an entitled line (the
  // customer bought a pool product while the old choice sat in the cart) — the
  // server then gifts the bought line and the popup must stay closed.
  function swapFromCart(handle) {
    var line = choiceLine(state.cart);
    if (!line) {
      openFromCart(handle);
      return;
    }
    // Decide on a fresh GET snapshot: removeChoice with a stale line key would
    // fail (HTTP 400) and the swap would leave the old choice next to the newly
    // picked one.
    withFreshCart(function (fresh) {
      if (isFullSnapshot(fresh)) state.cart = fresh;
      var current = choiceLine(state.cart);
      if (!current) {
        openFromCart(handle); // line already gone — offer the popup right away
        return;
      }
      removeChoice(current, function () {
        openFromCart(handle);
      });
    });
  }

  // ---------------------------------------------------------------- decisions

  // Displaced counter-proof (rule 3c): at least one OTHER line carries a
  // line-level discount allocation — proof that product discounts are being
  // granted in this order while our line was skipped. Without this proof a
  // full-price choice line is the EXPECTED state of the metaobject-only
  // staging phase (no BxGY yet, spec §8 Phase 6) and must survive.
  function otherLinesHaveAllocations(cart) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (isChoiceLine(items[i])) continue;
      var allocs = items[i].line_level_discount_allocations;
      if (allocs && allocs.length) return true;
    }
    return false;
  }

  function evaluate() {
    if (!state.cart) return;

    // 1) foreign code gift — platform backoff (non-Plus): the customer already
    //    holds (or reserved) the order's only free unit via a code gift
    if (foreignGiftLine(state.cart) || activeGiftCodeSession(state.cart)) {
      var mine2 = choiceLine(state.cart);
      if (mine2) removeChoice(mine2);
      if (modalOpen()) {
        hideModal();
        for (var s2 = 0; s2 < state.actions.length; s2++) clearSeen(state.actions[s2].handle);
      }
      return;
    }

    // 2) a chosen line exists — auto-remove rules
    var line = choiceLine(state.cart);
    if (line) {
      var lineAction = findAction(line.properties[GIFT_PROP]);

      // 2b) action vanished from the catalog (paused/ended) — Aseli leaves the
      //     line at an Infinity limit; MAMPE removes it + one-shot notice
      if (!lineAction) {
        storeNotice('ended');
        removeChoice(line);
        return;
      }

      // 2c) displaced: charged at full price while other lines get product
      //     discounts — a competing discount took the free unit (spec §9)
      var allocs = line.line_level_discount_allocations;
      if (line.final_line_price > 0 && !(allocs && allocs.length) && otherLinesHaveAllocations(state.cart)) {
        removeChoice(line); // silent — the cart strip re-renders without the pick
        return;
      }

      // below threshold on the LOOSE base — the checkout would drop it too
      // (configurators/tickets/vouchers never count, see looseBase)
      if (looseBase(state.cart) < lineAction.threshold) {
        removeChoice(line);
        return;
      }
      if (state.notice === 'ended') {
        state.notice = null; // action is back / line still valid — retire notice
        fillNotices();
      }
      return;
    }
    if (state.notice === 'ended') {
      state.notice = null; // no choice line left — the ended notice had its moment
      fillNotices();
    }

    if (!state.actions.length) return; // no active action — nothing further to do

    // 3) below-threshold actions get their seen flag cleared -> next crossing
    //    pops again (strict base: symmetric to the popup gate below)
    var base = strictBase(state.cart);
    for (var a = 0; a < state.actions.length; a++) {
      if (base < state.actions[a].threshold) clearSeen(state.actions[a].handle);
    }

    // 4) show for the HIGHEST threshold that is met (INCLUSIVE of active
    //    coupons — strict base), unseen, and where NO entitled line sits in the
    //    cart yet: an entitled line already receives — or would claim — the
    //    free unit, so the popup would promise a gift the server charges for.
    var best = null;
    for (var i = 0; i < state.actions.length; i++) {
      var action = state.actions[i];
      if (isSeen(action.handle)) continue;
      if (base < action.threshold) continue;
      if (entitledLine(state.cart, action)) continue;
      if (!best || action.threshold > best.threshold) best = action;
    }
    if (!best) return;

    // No deferral: the popup renders right away, layered over Impact's open
    // cart drawer (z 1100 > 999) — customers heading straight to checkout
    // from the drawer still see their gift (user finding 2026-10-21).
    renderPopup(best);
  }

  // ---------------------------------------------------------------- events / init

  document.addEventListener('click', function (event) {
    var select = event.target.closest(SELECTORS.select);
    if (select) {
      addChoice(select);
      return;
    }
    var open = event.target.closest(SELECTORS.open);
    if (open) {
      openFromCart(open.getAttribute('data-gc-action'));
      return;
    }
    var swap = event.target.closest(SELECTORS.swap);
    if (swap) {
      swapFromCart(swap.getAttribute('data-gc-action'));
      return;
    }
    if (event.target.closest(SELECTORS.dismiss)) {
      hideModal();
      return;
    }
    // backdrop close — Impact's dialog-element only hides on clicks OUTSIDE the
    // element itself, our overlay sits INSIDE it
    if (event.target.closest(SELECTORS.overlay)) {
      hideModal();
      return;
    }
    // Choice-line remove button (rendered by line-item.liquid instead of a
    // link — Impact's AJAX interception binds to <line-item-quantity>, which
    // choice lines don't have). Removes ONLY the line: no code session exists
    // for the automatic gift, nothing else to clean up.
    var lineRemove = event.target.closest(SELECTORS.lineRemove);
    if (lineRemove) {
      event.preventDefault();
      withFreshCart(function () {
        var current = choiceLine(state.cart);
        if (current) removeChoice(current);
      });
    }
  });

  function onCartChange(event) {
    // Impact's catch-all cart event (detail: {cart, baseEvent}) — fires on
    // theme-originated additions/changes/removals, NOT on our own raw fetches.
    var detail = (event && event.detail) || {};
    if (detail.cart) state.cart = detail.cart;
    if (!isFullSnapshot(state.cart)) {
      refreshCartThenEvaluate(); // partial payload — decide on fresh data
      return;
    }
    evaluate();
  }

  function onCartRefresh() {
    // Fired by the code-gift widget (refreshTheme) after ITS mutations — our
    // replacement for Aseli's RETIRE_EVENT bridge, without touching
    // mampe-cart-gift.js. Also fires after our own refreshTheme: the extra
    // GET is cheap and the refreshing-guard throttles it.
    refreshCartThenEvaluate();
  }

  function init() {
    readActions();
    giftCodes = readGiftCodes();
    watchModal();
    window.addEventListener('resize', function () {
      if (confetti.running) {
        stopConfetti();
        startConfetti(); // re-measure strips + canvas for the new viewport
      }
    });
    // Impact swaps cart regions via innerHTML on every change — hydrate NEW
    // notice slots only (guarded writes, rAF-batched, no observer loops).
    var hydratePending = false;
    var observer = new MutationObserver(function () {
      if (hydratePending) return;
      hydratePending = true;
      requestAnimationFrame(function () {
        hydratePending = false;
        hydrateNoticeSlots();
      });
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });

    document.addEventListener('cart:change', onCartChange);
    document.addEventListener('cart:refresh', onCartRefresh);

    fetch(routes().cartUrl + '.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        state.cart = cart;
        consumeNotice();
        fillNotices();
        evaluate();
      })
      .catch(function () {
        /* decisions re-run on the next cart event */
      });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
